import { Mail, ArrowRight, Phone, Users, ClipboardList, Timer, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useBookDemo } from "@/context/BookDemoContext";
import { useIndustryModal } from "@/context/IndustryModalContext";
import { INDUSTRIES } from "@/data/industries";

const SHARED_NEEDS = [
    { icon: Phone, title: "Kundekontakt" },
    { icon: ClipboardList, title: "Jobstyring" },
    { icon: Users, title: "Team-opgaver" },
    { icon: Timer, title: "Tid og materialer" },
    { icon: BarChart3, title: "Rapportering" },
];

export default function Brancher() {
    const { open: openDemo } = useBookDemo();
    const { open: openIndustry } = useIndustryModal();

    return (
        <div data-testid="page-brancher">
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
                <div className="absolute -top-20 left-1/3 w-[420px] h-[420px] rounded-full bg-indigo-200/30 blur-3xl pointer-events-none" />

                <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-12 text-center">
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-[11px] font-semibold uppercase tracking-wider border border-blue-100">
                        Brancher
                    </span>
                    <h1 className="mt-5 text-4xl sm:text-5xl lg:text-[3.25rem] font-bold tracking-tight text-slate-900 leading-[1.05]">
                        Brancher{" "}
                        <span className="gradient-text">vi hjælper.</span>
                    </h1>
                    <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
                        MyServiceHelper kan passe til de fleste servicevirksomheder. Klik på en branche for at se mere.
                    </p>
                </div>
            </section>

            <section className="pb-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {INDUSTRIES.map((ind) => {
                            const Icon = ind.icon;
                            return (
                                <button
                                    type="button"
                                    key={ind.key}
                                    onClick={() => openIndustry(ind.key)}
                                    data-testid={`brancher-card-${ind.key}`}
                                    className="group text-left bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-lg hover:-translate-y-0.5 hover:border-blue-300 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-blue-50 to-purple-50 text-blue-600 inline-flex items-center justify-center ring-1 ring-blue-100">
                                            <Icon className="w-5 h-5" />
                                        </div>
                                        <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-blue-600 group-hover:translate-x-0.5 transition-all" />
                                    </div>
                                    <h3 className="mt-4 text-lg font-semibold text-slate-900">{ind.name}</h3>
                                    <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{ind.short}</p>
                                    <p className="mt-4 text-xs font-semibold text-blue-700 group-hover:text-blue-800 inline-flex items-center gap-1">
                                        Læs mere <ArrowRight className="w-3 h-3" />
                                    </p>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="pb-16">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="rounded-2xl bg-white border border-slate-200 p-8 sm:p-10 text-center">
                        <h3 className="text-2xl font-bold text-slate-900">Er du i tvivl?</h3>
                        <p className="mt-3 text-slate-600 max-w-xl mx-auto">
                            Skriv eller ring — vi hjælper gerne og finder hurtigt ud af, om MyServiceHelper passer til jer.
                        </p>
                        <a
                            href="mailto:Xenius@Gregersen.dk"
                            data-testid="brancher-email-link"
                            className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 text-blue-700 border border-blue-100 hover:bg-blue-100 transition-colors font-medium"
                        >
                            <Mail className="w-4 h-4" />
                            Xenius@Gregersen.dk
                        </a>
                    </div>
                </div>
            </section>

            <section className="pb-20 bg-white border-y border-slate-200 py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center max-w-2xl mx-auto">
                        <span className="inline-flex items-center px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-semibold uppercase tracking-wider border border-emerald-100">
                            Fælles behov
                        </span>
                        <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-slate-900">
                            På tværs af brancher mødes vi i de samme behov
                        </h2>
                    </div>

                    <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                        {SHARED_NEEDS.map((n, i) => {
                            const Icon = n.icon;
                            return (
                                <div key={i} data-testid={`shared-need-${i}`} className="bg-slate-50 rounded-2xl border border-slate-200 p-5 text-center">
                                    <div className="mx-auto w-10 h-10 rounded-xl bg-white text-blue-600 ring-1 ring-blue-100 inline-flex items-center justify-center">
                                        <Icon className="w-5 h-5" />
                                    </div>
                                    <p className="mt-3 text-sm font-semibold text-slate-900">{n.title}</p>
                                </div>
                            );
                        })}
                    </div>

                    <div className="mt-10 text-center">
                        <Button data-testid="brancher-cta-btn" onClick={() => openDemo()} className="rounded-full h-12 px-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-md">
                            Book gratis demo <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    </div>
                </div>
            </section>
        </div>
    );
}
